import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { Appointment, SchedulesService } from 'src/app/services/schedules.service';
import { ConfigOrder, ConfigOrdersService } from 'src/app/services/config-orders.service';
import { CustomersService } from 'src/app/services/customers.service';
import { ServiceOrdersService } from 'src/app/services/service-orders.service';
import { AuthService } from 'src/app/services/auth.service';
import { UsersService } from 'src/app/services/users.service';
import { ErrorService } from 'src/app/shared/services/error.service';
import { Customer } from 'src/app/models/Customer';
import {
  VehicleSelectionModalComponent,
  VehicleSelection
} from 'src/app/components/service-orders/vehicle-selection-modal/vehicle-selection-modal.component';

@Component({
  selector: 'app-appointment-third-party-os-form',
  templateUrl: './appointment-third-party-os-form.component.html',
  styleUrls: ['./appointment-third-party-os-form.component.css']
})
export class AppointmentThirdPartyOsFormComponent implements OnInit {
  isLoading = true;
  submitting = false;
  error: string | null = null;

  appointmentId: number | null = null;
  appointment: Appointment | null = null;
  customer: Customer | null = null;

  thirdPartyConfigs: ConfigOrder[] = [];

  selectedVehicle: VehicleSelection | null = null;

  @ViewChild('vehicleModal') vehicleModal!: VehicleSelectionModalComponent;

  form!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private schedulesService: SchedulesService,
    private configOrdersService: ConfigOrdersService,
    private customersService: CustomersService,
    private serviceOrdersService: ServiceOrdersService,
    private authService: AuthService,
    private usersService: UsersService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      clienteId: [{ value: null, disabled: true }],
      clienteNome: [{ value: '', disabled: true }],
      configOrderId: [null, Validators.required],
      typeOS: [''],
      motivoAbertura: ['', Validators.required],
      openingDate: ['', Validators.required],
      veiculoId: [null],
      veiculoPlaca: [{ value: '', disabled: true }],
      observacoes: ['']
    });

    const idParam = this.route.snapshot.paramMap.get('appointmentId');
    if (!idParam) {
      this.error = 'Agendamento não informado.';
      this.isLoading = false;
      return;
    }
    this.appointmentId = Number(idParam);

    this.loadInitialData();
  }

  private loadInitialData(): void {
    this.isLoading = true;
    this.error = null;

    forkJoin({
      appointment: this.schedulesService.getAppointmentById(this.appointmentId!).pipe(
        catchError(err => {
          console.error('Erro ao carregar agendamento:', err);
          return of(null);
        })
      ),
      configs: this.configOrdersService.getAll().pipe(
        catchError(err => {
          console.error('Erro ao carregar configurações de OS:', err);
          return of([] as ConfigOrder[]);
        })
      )
    }).subscribe(({ appointment, configs }) => {
      if (!appointment) {
        this.error = 'Não foi possível carregar o agendamento.';
        this.isLoading = false;
        return;
      }

      this.appointment = appointment;
      this.thirdPartyConfigs = (configs || []).filter(
        c => c.isThirdParty === true && c.active !== false
      );

      this.prefillForm(appointment);

      if (appointment.CustomerId) {
        this.customersService.getById(appointment.CustomerId).pipe(
          catchError(err => {
            console.error('Erro ao carregar cliente:', err);
            return of(null);
          })
        ).subscribe(customer => {
          this.customer = customer;
          if (customer) {
            this.form.patchValue({
              clienteId: customer.id,
              clienteNome: customer.name
            });
          }
          this.isLoading = false;
        });
      } else {
        this.isLoading = false;
      }
    });
  }

  private prefillForm(appointment: Appointment): void {
    const description = (appointment.Description || appointment.Subject || '').trim();
    const openingDate = this.composeOpeningDate(appointment.Date, appointment.StartTime);

    this.form.patchValue({
      clienteId: appointment.CustomerId ?? null,
      motivoAbertura: description,
      openingDate
    });
  }

  /**
   * Compose a value compatible with `<input type="datetime-local">` (yyyy-MM-ddTHH:mm).
   */
  private composeOpeningDate(date: string, startTime: string): string {
    if (!date) {
      return '';
    }
    const datePart = date.includes('T') ? date.split('T')[0] : date.substring(0, 10);
    const timePart = (startTime || '00:00').substring(0, 5);
    return `${datePart}T${timePart}`;
  }

  onConfigOrderChange(): void {
    const configOrderId = this.form.get('configOrderId')?.value;
    const config = this.thirdPartyConfigs.find(c => c.id === Number(configOrderId));
    this.form.patchValue({ typeOS: config?.typeOS ?? '' });
  }

  openVehicleModal(): void {
    this.vehicleModal?.open();
  }

  onVehicleSelected(vehicle: VehicleSelection): void {
    this.selectedVehicle = vehicle;
    this.form.patchValue({
      veiculoId: vehicle.id,
      veiculoPlaca: `${vehicle.placa} - ${vehicle.marca} ${vehicle.modelo}`
    });
  }

  clearVehicle(): void {
    this.selectedVehicle = null;
    this.form.patchValue({ veiculoId: null, veiculoPlaca: '' });
  }

  cancel(): void {
    this.router.navigate(['/appointments']);
  }

  submit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(k => this.form.get(k)?.markAsTouched());
      return;
    }

    if (!this.thirdPartyConfigs.length) {
      this.errorService.showErrorMessage(
        'Nenhuma configuração de OS de terceiros disponível. Cadastre uma configuração com "Terceiros" habilitado.'
      );
      return;
    }

    this.submitting = true;
    this.resolveCurrentUserDepartmentId()
      .pipe(take(1))
      .subscribe((departmentId) => {
        if (!departmentId) {
          this.submitting = false;
          this.errorService.showErrorMessage(
            'Não foi possível identificar o setor do usuário logado. Verifique o cadastro do usuário.'
          );
          return;
        }
        this.createOs(departmentId);
      });
  }

  private createOs(departmentId: number): void {
    const raw = this.form.getRawValue();
    const config = this.thirdPartyConfigs.find(c => c.id === Number(raw.configOrderId));

    const payload: any = {
      openingDate: this.toIsoDateTime(raw.openingDate) ?? new Date().toISOString(),
      description: raw.motivoAbertura || null,
      typeOS: config?.typeOS ?? raw.typeOS ?? null,
      configOrderId: config?.id ?? null,
      departmentId,
      DepartmentId: departmentId,
      customerId: this.toIntOrNull(raw.clienteId),
      vehicleId: this.toIntOrNull(raw.veiculoId),
      status: 'Aberta',
      isScheduled: true
    };

    if (raw.observacoes) {
      payload.notes = raw.observacoes;
    }

    this.serviceOrdersService.create(payload).subscribe({
      next: (created) => {
        this.markAppointmentAsOsGenerated(created?.id ?? null, () => {
          this.submitting = false;
          this.router.navigate(['/appointments']);
        });
      },
      error: (err) => {
        console.error('Erro ao criar OS de terceiros:', err);
        this.submitting = false;
      }
    });
  }

  private markAppointmentAsOsGenerated(serviceOrderId: number | null, done: () => void): void {
    if (!this.appointment || !this.appointmentId) {
      done();
      return;
    }

    const userName = this.authService.currentUserValue?.username || 'Sistema';
    const updated: Appointment = {
      ...this.appointment,
      ServiceOrderId: serviceOrderId ?? this.appointment.ServiceOrderId ?? null,
      UpdatedBy: userName
    };

    this.schedulesService.updateAppointment(this.appointmentId, updated).pipe(
      catchError(err => {
        console.error('Erro ao atualizar status do agendamento:', err);
        return of(null);
      })
    ).subscribe(() => done());
  }

  private resolveCurrentUserDepartmentId(): Observable<number | null> {
    const tokenDepartmentId = this.authService.getDepartmentIdFromToken();
    if (tokenDepartmentId) {
      return of(tokenDepartmentId);
    }

    const userId = this.parsePositiveNumber(this.authService.getUserIdFromToken());
    if (!userId) {
      return of(null);
    }

    return this.usersService.getUserById(userId).pipe(
      switchMap((profile) => {
        const departmentId = this.extractDepartmentIdFromProfile(profile);
        if (departmentId) {
          return of(departmentId);
        }

        const staffId = this.extractStaffIdFromProfile(profile);
        if (!staffId || !this.usersService.staffService) {
          return of(null);
        }

        return this.usersService.staffService.getStaffById(staffId).pipe(
          map((staff: any) => this.extractDepartmentIdFromProfile(staff)),
          catchError((error) => {
            console.error('Erro ao buscar staff do usuário logado:', error);
            return of(null);
          })
        );
      }),
      catchError((error) => {
        console.error('Erro ao buscar perfil do usuário logado:', error);
        return of(null);
      })
    );
  }

  private parsePositiveNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private extractDepartmentIdFromProfile(profile: any): number | null {
    return this.parsePositiveNumber(
      profile?.DepartmentId
      ?? profile?.departmentId
      ?? profile?.UnidadeId
      ?? profile?.unidadeId
      ?? profile?.Staff?.DepartmentId
      ?? profile?.Staff?.departmentId
      ?? profile?.Staff?.Staff?.DepartmentId
      ?? profile?.Staff?.Staff?.departmentId
      ?? profile?.staff?.DepartmentId
      ?? profile?.staff?.departmentId
      ?? profile?.staff?.staff?.DepartmentId
      ?? profile?.staff?.staff?.departmentId
    );
  }

  private extractStaffIdFromProfile(profile: any): number | null {
    return this.parsePositiveNumber(
      profile?.StaffId
      ?? profile?.staffId
      ?? profile?.Staff?.Id
      ?? profile?.Staff?.StaffId
      ?? profile?.Staff?.Staff?.Id
      ?? profile?.Staff?.Staff?.StaffId
      ?? profile?.staff?.Id
      ?? profile?.staff?.StaffId
      ?? profile?.staff?.staff?.Id
      ?? profile?.staff?.staff?.StaffId
      ?? (this.authService.currentUserValue as any)?.staffId
      ?? (this.authService.currentUserValue as any)?.StaffId
    );
  }

  private toIsoDateTime(value: string | null | undefined): string | null {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  }

  private toIntOrNull(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
}
